import { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  useUpdatePart,
  type Part,
  type UpdatePartPayload,
} from "../services/PartService";
import type { PartGroup } from "../services/PartGroupService";
import { NucToast } from "src/reusable/components/Toast";
import { extractErrorDetail } from "../utils/errorDetail";
import FeatureRowsEditor from "./FeatureRowsEditor";
import {
  featuresToRows,
  rowsToFeatures,
  type FeatureSheetRow,
} from "../config/featureRows";

interface EditPartModalProps {
  open: boolean;
  onClose: () => void;
  part: Part;
  partGroups: PartGroup[];
}

const EditPartModal = ({
  open,
  onClose,
  part,
  partGroups,
}: EditPartModalProps) => {
  const [name, setName] = useState("");
  const [partType, setPartType] = useState("");
  const [manufacturer, setManufacturer] = useState("");
  const [materialNumber, setMaterialNumber] = useState("");
  const [description, setDescription] = useState("");
  const [partGroupFk, setPartGroupFk] = useState<number | "">("");
  const [rows, setRows] = useState<FeatureSheetRow[]>([]);

  const updatePart = useUpdatePart();

  // Seed the form from the part whenever the modal (re)opens, so it always
  // reflects the latest loaded values and discards any prior unsaved edits.
  useEffect(() => {
    if (!open) return;
    setName(part.name ?? "");
    setPartType(part.part_type ?? "");
    setManufacturer(part.manufacturer ?? "");
    setMaterialNumber(part.material_number ?? "");
    setDescription(part.description ?? "");
    setPartGroupFk(part.part_group_fk ?? "");
    setRows(featuresToRows(part.features, part.feature_order));
  }, [open, part.id]);

  const handleSubmit = () => {
    const trimmedName = name.trim();
    if (!trimmedName) return;

    // Build a dirty-diff: only send fields that actually changed. This avoids
    // an unnecessary LLM re-embed on a no-op save and keeps us clear of the
    // UpdatePart validators (never send name null/"" or features null).
    const payload: UpdatePartPayload = {};

    if (trimmedName !== part.name) payload.name = trimmedName;

    const nextPartType = partType.trim() || null;
    if (nextPartType !== (part.part_type ?? null)) {
      payload.part_type = nextPartType;
    }
    const nextManufacturer = manufacturer.trim() || null;
    if (nextManufacturer !== (part.manufacturer ?? null)) {
      payload.manufacturer = nextManufacturer;
    }
    const nextMaterialNumber = materialNumber.trim() || null;
    if (nextMaterialNumber !== (part.material_number ?? null)) {
      payload.material_number = nextMaterialNumber;
    }
    const nextDescription = description.trim() || null;
    if (nextDescription !== (part.description ?? null)) {
      payload.description = nextDescription;
    }
    const nextGroupFk = partGroupFk === "" ? null : partGroupFk;
    if (nextGroupFk !== (part.part_group_fk ?? null)) {
      payload.part_group_fk = nextGroupFk;
    }

    // Compare against the same normalized+ordered baseline the editor started
    // from, so value edits, adds, deletes, AND reorders are all detected (the
    // backend derives feature_order from the features object's key order).
    const nextFeatures = rowsToFeatures(rows);
    const baselineFeatures = rowsToFeatures(
      featuresToRows(part.features, part.feature_order),
    );
    if (!featuresEqual(nextFeatures, baselineFeatures)) {
      payload.features = nextFeatures;
    }

    // Nothing changed — close without hitting the API.
    if (Object.keys(payload).length === 0) {
      onClose();
      return;
    }

    updatePart.mutate(
      { partId: part.id, payload },
      {
        onSuccess: () => {
          NucToast({
            message: `Part "${trimmedName}" updated.`,
            type: "success",
            hideProgressBar: false,
          });
          onClose();
        },
        onError: (err) => {
          const detail = extractErrorDetail(err);
          console.error(`Failed to update part ${part.id}: ${detail}`, err);
          NucToast({
            message: `Failed to update part: ${detail}`,
            type: "error",
            hideProgressBar: false,
          });
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      onClose={updatePart.isLoading ? undefined : onClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>Edit Part</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Part Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            fullWidth
            autoFocus
            disabled={updatePart.isLoading}
          />
          <TextField
            label="Part Type"
            value={partType}
            onChange={(e) => setPartType(e.target.value)}
            fullWidth
            placeholder="e.g., Valve, Flange, Pump"
            disabled={updatePart.isLoading}
          />
          <TextField
            label="Manufacturer"
            value={manufacturer}
            onChange={(e) => setManufacturer(e.target.value)}
            fullWidth
            disabled={updatePart.isLoading}
          />
          <TextField
            label="Material Number"
            value={materialNumber}
            onChange={(e) => setMaterialNumber(e.target.value)}
            fullWidth
            disabled={updatePart.isLoading}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={2}
            disabled={updatePart.isLoading}
          />
          <FormControl fullWidth disabled={updatePart.isLoading}>
            <InputLabel>Part Group</InputLabel>
            <Select
              value={partGroupFk}
              label="Part Group"
              onChange={(e) => {
                const raw = e.target.value;
                setPartGroupFk(raw === "" ? "" : Number(raw));
              }}
            >
              <MenuItem value="">None</MenuItem>
              {partGroups.map((g) => (
                <MenuItem key={g.id} value={g.id}>
                  {g.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Divider />
          <Typography variant="subtitle2" fontWeight={600}>
            Features
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: -1 }}>
            Editing features re-embeds the part; updated matches appear once the
            job completes.
          </Typography>
          <FeatureRowsEditor
            rows={rows}
            onRowsChange={setRows}
            disabled={updatePart.isLoading}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={updatePart.isLoading}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!name.trim() || updatePart.isLoading}
          startIcon={
            updatePart.isLoading ? (
              <CircularProgress size={16} color="inherit" />
            ) : null
          }
        >
          {updatePart.isLoading ? "Saving..." : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

/** Order-sensitive key/value equality for two feature records. */
const featuresEqual = (
  a: Record<string, string>,
  b: Record<string, string>,
): boolean => {
  const aKeys = Object.keys(a);
  const bKeys = Object.keys(b);
  if (aKeys.length !== bKeys.length) return false;
  return aKeys.every((k, i) => k === bKeys[i] && a[k] === b[k]);
};

export default EditPartModal;
